import { Deferred, Head, router, usePage } from "@inertiajs/react";
import { AnimatePresence, motion } from "framer-motion";
import {
	ArrowDownUpIcon,
	Check,
	ChevronsUpDown,
	Clock3Icon,
	ListFilterIcon,
	LoaderCircleIcon,
	MoreHorizontal,
	Search,
	Settings,
	X,
	XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { PageContainer } from "@/components/admin-portal/shared/page-layout";
import MultiSelectBadges from "@/components/shared/multi-select-badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { getCsrfToken } from "@/lib/csrf";
import { cn, formatDateTime, populateQueryParams } from "@/lib/utils";
import type { GlobalPageProps as BaseGlobalPageProps } from "@/types/globals";
import type { Metadata } from "@/types/pagy";

interface ServiceOption {
	id: number;
	name: string;
	label: string;
	packages?: { id: number; name: string }[];
}

interface UnassignedVisit {
	id: string;
	registrationNumber: string;
	visitNumber: number;
	totalPackageVisits: number;
	appointmentDateTime: string | null;
	createdAt: string;
	orderId: string | null;
	invoiceNumber: string | null;
	patient: { id: number; name: string } | null;
	location: { id: number; city: string } | null;
	service: { id: number; name: string } | null;
	package: { id: number; name: string } | null;
	latestBlastAt: string | null;
	latestBlastChannel: string | null;
}

interface UnassignedVisitsPageProps {
	appointments?: {
		data: { date: string; schedules: UnassignedVisit[] }[];
		metadata: Metadata;
	};
	filterOptionsData?: {
		sortList: ["newest", "oldest", "upcoming_visit", "furthest_visit"];
		services?: ServiceOption[];
	};
}

interface UnassignedVisitsGlobalPageProps
	extends BaseGlobalPageProps,
		UnassignedVisitsPageProps {
	[key: string]: any;
}

type BlastChannel = "TELEGRAM" | "WHATSAPP" | "ALL";

type FailedReblastItem = {
	appointmentId: string;
	registrationNumber: string | null;
	errorMessage: string;
};

const REBLAST_CHANNEL_OPTIONS: BlastChannel[] = ["WHATSAPP", "TELEGRAM", "ALL"];

const pickFirstString = (...values: unknown[]): string | null => {
	for (const value of values) {
		if (typeof value === "string" && value.trim()) return value.trim();
		if (typeof value === "number") return String(value);
	}

	return null;
};

const parseFailedReblastItems = (
	results: unknown[],
	fallbackErrorMessage: string,
): FailedReblastItem[] => {
	return results
		.map((item) => {
			if (!item || typeof item !== "object") return null;

			const result = item as Record<string, unknown>;
			const status = pickFirstString(
				result.status,
				result.blast_status,
				result.blastStatus,
			)?.toUpperCase();
			const errorMessage = pickFirstString(
				result.error_message,
				result.errorMessage,
				result.error,
				result.message,
				result.reason,
			);
			const success =
				typeof result.success === "boolean" ? result.success : null;
			const isFailedStatus = status
				? status.includes("FAILED") || status.includes("ERROR")
				: false;
			const isFailed =
				success === false || isFailedStatus || Boolean(errorMessage);

			if (!isFailed) return null;

			return {
				appointmentId:
					pickFirstString(
						result.appointment_id,
						result.appointmentId,
						result.id,
					) || "-",
				registrationNumber: pickFirstString(
					result.registration_number,
					result.registrationNumber,
					result.reg_no,
				),
				errorMessage: errorMessage || fallbackErrorMessage,
			};
		})
		.filter((item): item is FailedReblastItem => Boolean(item));
};

export default function UnassignedVisits() {
	const { props: globalProps, url: pageURL } =
		usePage<UnassignedVisitsGlobalPageProps>();
	const { t, i18n } = useTranslation("appointments");
	const ordersPageUrl =
		globalProps?.adminPortal?.router?.adminPortal?.appointment?.orders;
	const apptPageUrl =
		globalProps?.adminPortal?.router?.adminPortal?.appointment?.index;
	const mitraBlastConfigsUrl =
		globalProps?.adminPortal?.router?.adminPortal?.mitraBlastConfigs?.index;

	const groups = globalProps.appointments?.data || [];
	const metadata = globalProps.appointments?.metadata || null;
	const currentLocale = i18n.language;

	const visits = useMemo(
		() => groups.flatMap((group) => group.schedules),
		[groups],
	);

	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [isReblastDrawerOpen, setIsReblastDrawerOpen] = useState(false);
	const [selectedBlastChannel, setSelectedBlastChannel] =
		useState<BlastChannel>("WHATSAPP");
	const [isSubmittingReblast, setIsSubmittingReblast] = useState(false);
	const [reblastSubmitError, setReblastSubmitError] = useState<string | null>(
		null,
	);
	const [failedReblastItems, setFailedReblastItems] = useState<
		FailedReblastItem[]
	>([]);

	const filterSignature = useMemo(() => {
		const cq = globalProps?.adminPortal?.currentQuery;
		return [
			metadata?.page,
			metadata?.limit,
			cq?.sort,
			cq?.search,
			cq?.serviceIds,
			cq?.packageIds,
		].join("|");
	}, [globalProps?.adminPortal?.currentQuery, metadata?.limit, metadata?.page]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: stable signature derived from page/sort/filter state
	useEffect(() => {
		setSelectedIds(new Set());
		setIsReblastDrawerOpen(false);
		setReblastSubmitError(null);
		setFailedReblastItems([]);
	}, [filterSignature]);

	const allSelected =
		visits.length > 0 && visits.every((v) => selectedIds.has(v.id));
	const someSelected =
		visits.some((v) => selectedIds.has(v.id)) && !allSelected;

	const toggleSelectAll = () => {
		if (allSelected) {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				visits.forEach((v) => next.delete(v.id));
				return next;
			});
		} else {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				visits.forEach((v) => next.add(v.id));
				return next;
			});
		}
	};

	const toggleSelectOne = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const goToPage = (url?: string | null) => {
		if (!url) return;

		if (searchDebounceRef.current) {
			clearTimeout(searchDebounceRef.current);
			searchDebounceRef.current = null;
			setIsSearching(false);
		}

		router.get(
			url,
			{},
			{
				preserveScroll: true,
				preserveState: true,
				only: ["adminPortal", "flash", "errors", "appointments"],
			},
		);
	};

	const updatePageParam = (params: { page?: number; limit?: number }) => {
		const { fullUrl } = populateQueryParams(pageURL, params);
		goToPage(fullUrl);
	};

	// * sort management state with animation
	const [isSorting, setIsSorting] = useState(false);
	const sortBy = useMemo(() => {
		const currentQuery = globalProps?.adminPortal?.currentQuery;
		return currentQuery?.sort || "";
	}, [globalProps?.adminPortal?.currentQuery]);
	const sortList = useMemo(
		() => globalProps.filterOptionsData?.sortList || [],
		[globalProps.filterOptionsData?.sortList],
	);
	const onChangeSort = useCallback(
		(value: string) => {
			if (searchDebounceRef.current) {
				clearTimeout(searchDebounceRef.current);
				searchDebounceRef.current = null;
				setIsSearching(false);
			}

			const { fullUrl } = populateQueryParams(pageURL, {
				sort: value === "clear" ? null : value,
			});
			router.get(
				fullUrl,
				{},
				{
					preserveScroll: true,
					preserveState: true,
					only: ["adminPortal", "flash", "errors", "appointments"],
					onStart: () => {
						setIsSorting(true);
					},
					onFinish: () => {
						setTimeout(() => {
							setIsSorting(false);
						}, 250);
					},
				},
			);
		},
		[pageURL],
	);

	// * filter visibility state
	const [isShowFilter, setIsShowFilter] = useState(false);
	const handleToggleFilter = () => {
		setIsShowFilter((prev) => !prev);
	};

	// * search filter state
	const [isSearching, setIsSearching] = useState(false);
	const [searchQuery, setSearchQuery] = useState(() => {
		const currentQuery = globalProps?.adminPortal?.currentQuery;
		return currentQuery?.search || "";
	});
	const [serviceIds, setServiceIds] = useState(() => {
		const currentQuery = globalProps?.adminPortal?.currentQuery;
		return currentQuery?.serviceIds || "";
	});
	const [packageIds, setPackageIds] = useState(() => {
		const currentQuery = globalProps?.adminPortal?.currentQuery;
		return currentQuery?.packageIds || "";
	});

	// Sync filter inputs when page reloads (back/forward, pagination, etc.)
	useEffect(() => {
		const cq = globalProps?.adminPortal?.currentQuery;
		setSearchQuery(cq?.search || "");
		setServiceIds(cq?.serviceIds || "");
		setPackageIds(cq?.packageIds || "");
	}, [globalProps?.adminPortal?.currentQuery]);

	const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const executeNavigation = useCallback((url: string) => {
		if (searchDebounceRef.current) {
			clearTimeout(searchDebounceRef.current);
			searchDebounceRef.current = null;
			setIsSearching(false);
		}
		router.get(
			url,
			{},
			{
				preserveScroll: true,
				preserveState: true,
				only: ["adminPortal", "flash", "errors", "appointments"],
				onStart: () => setIsSearching(true),
				onFinish: () => {
					setTimeout(() => setIsSearching(false), 250);
				},
			},
		);
	}, []);

	const debouncedNavigate = useCallback((url: string, delay: number) => {
		if (searchDebounceRef.current) {
			clearTimeout(searchDebounceRef.current);
		}
		setIsSearching(true);
		searchDebounceRef.current = setTimeout(() => {
			searchDebounceRef.current = null;
			router.get(
				url,
				{},
				{
					preserveScroll: true,
					preserveState: true,
					only: ["adminPortal", "flash", "errors", "appointments"],
					onStart: () => setIsSearching(true),
					onFinish: () => {
						setTimeout(() => setIsSearching(false), 250);
					},
				},
			);
		}, delay);
	}, []);

	// * service/package filter options
	const availableServices = useMemo(
		() =>
			globalProps?.filterOptionsData?.services?.map((service) => ({
				...service,
				label: service.name.replaceAll("_", " "),
			})) || [],
		[globalProps?.filterOptionsData?.services],
	);
	const availablePackages = useMemo(() => {
		return (
			globalProps?.filterOptionsData?.services
				?.flatMap((service) =>
					(service.packages || []).map((pkg) => ({
						...pkg,
						label: pkg.name.replaceAll("_", " "),
						group: service.name.replaceAll("_", " "),
					})),
				)
				?.filter(Boolean) || []
		);
	}, [globalProps?.filterOptionsData?.services]);
	const selectedServiceIds = useMemo(
		() =>
			serviceIds
				?.split(",")
				.map((id) => id.trim())
				.filter(Boolean),
		[serviceIds],
	);
	const selectedPackageIds = useMemo(
		() =>
			packageIds
				?.split(",")
				.map((id) => id.trim())
				.filter(Boolean),
		[packageIds],
	);
	const selectedServiceOptions = useMemo(
		() =>
			availableServices
				.filter((service) => selectedServiceIds.includes(String(service.id)))
				.map((service) => ({ ...service, label: service.label })),
		[availableServices, selectedServiceIds],
	);
	const selectedPackageOptions = useMemo(() => {
		return availablePackages.filter((pkg) =>
			selectedPackageIds.includes(String(pkg.id)),
		);
	}, [availablePackages, selectedPackageIds]);
	const groupedPackageOptions = useMemo(
		() =>
			availableServices?.map((service) => ({
				header: service.name.replaceAll("_", " "),
				packages: service.packages || [],
			})),
		[availableServices],
	);

	const handleFilterBy = useCallback(
		({
			value,
			type,
		}: {
			value: string;
			type: "search" | "serviceIds" | "packageIds";
		}) => {
			if (type === "search") setSearchQuery(value);
			if (type === "serviceIds") setServiceIds(value);
			if (type === "packageIds") setPackageIds(value);

			const nextSearch = type === "search" ? value : searchQuery;
			const nextServiceIds = type === "serviceIds" ? value : serviceIds;
			const nextPackageIds = type === "packageIds" ? value : packageIds;

			const { fullUrl } = populateQueryParams(pageURL, {
				search: nextSearch || null,
				service_ids: nextServiceIds || null,
				package_ids: nextPackageIds || null,
				page: null,
				limit: null,
			});
			const delay = type === "search" ? 300 : 150;
			debouncedNavigate(fullUrl, delay);
		},
		[pageURL, searchQuery, serviceIds, packageIds, debouncedNavigate],
	);

	const handleToggleServiceSelection = useCallback(
		(id: string) => {
			const currentSelection = selectedServiceIds ?? [];
			const isSelected = currentSelection.includes(id);
			const updatedSelection = isSelected
				? currentSelection.filter((sid) => sid !== id)
				: [...currentSelection, id];

			handleFilterBy({
				type: "serviceIds",
				value: updatedSelection.filter(Boolean).join(","),
			});
		},
		[selectedServiceIds, handleFilterBy],
	);

	const handleTogglePackageSelection = useCallback(
		(id: string) => {
			const currentSelection = selectedPackageIds ?? [];
			const isSelected = currentSelection.includes(id);
			const updatedSelection = isSelected
				? currentSelection.filter((pid) => pid !== id)
				: [...currentSelection, id];

			handleFilterBy({
				type: "packageIds",
				value: updatedSelection.filter(Boolean).join(","),
			});
		},
		[selectedPackageIds, handleFilterBy],
	);

	const handleSearchFilter = useCallback(
		(value: string) => {
			handleFilterBy({ type: "search", value });
		},
		[handleFilterBy],
	);

	const onResetSearch = useCallback(() => {
		setSearchQuery("");
		setServiceIds("");
		setPackageIds("");

		const { fullUrl } = populateQueryParams(pageURL, {
			search: null,
			service_ids: null,
			package_ids: null,
			page: null,
			limit: null,
		});
		executeNavigation(fullUrl);
	}, [pageURL, executeNavigation]);

	const onReblastDrawerOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (isSubmittingReblast) return;

			setIsReblastDrawerOpen(nextOpen);
			if (!nextOpen) setReblastSubmitError(null);
		},
		[isSubmittingReblast],
	);

	const submitManualReblast = useCallback(async () => {
		const appointmentIds = Array.from(selectedIds);
		if (appointmentIds.length === 0) return;

		setIsSubmittingReblast(true);
		setReblastSubmitError(null);
		setFailedReblastItems([]);

		try {
			const response = await fetch("/api/v1/notifications/resend/mitra-blast", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": getCsrfToken(),
				},
				body: JSON.stringify({
					appointment_ids: appointmentIds,
					channel: selectedBlastChannel,
				}),
			});

			const responseBody = (await response.json().catch(() => ({}))) as {
				success?: boolean;
				error?: string;
				total_sent?: number;
				total_failed?: number;
				totalSent?: number;
				totalFailed?: number;
				results?: unknown[];
			};

			if (!response.ok || responseBody.success === false) {
				const message =
					responseBody.error || t("unassigned_visits.reblast.toast.error");
				setReblastSubmitError(message);
				toast.error(message);
				return;
			}

			const totalSent = Number(
				responseBody.total_sent ?? responseBody.totalSent ?? 0,
			);
			const totalFailed = Number(
				responseBody.total_failed ?? responseBody.totalFailed ?? 0,
			);
			const results = Array.isArray(responseBody.results)
				? responseBody.results
				: [];
			const failedItems = parseFailedReblastItems(
				results,
				t("unassigned_visits.reblast.alert.default_error"),
			);

			if (totalFailed > 0 && failedItems.length === 0) {
				setFailedReblastItems([
					{
						appointmentId: "-",
						registrationNumber: null,
						errorMessage: t(
							"unassigned_visits.reblast.alert.failed_without_details",
							{ count: totalFailed },
						),
					},
				]);
			} else {
				setFailedReblastItems(failedItems);
			}

			toast.success(
				t("unassigned_visits.reblast.toast.summary", {
					sent: totalSent,
					failed: totalFailed,
				}),
			);

			setSelectedIds(new Set());
			setReblastSubmitError(null);
			setIsReblastDrawerOpen(false);
		} catch (error) {
			console.error("Manual reblast error:", error);
			const message = t("unassigned_visits.reblast.toast.error");
			setReblastSubmitError(message);
			toast.error(message);
		} finally {
			setIsSubmittingReblast(false);
		}
	}, [selectedIds, selectedBlastChannel, t]);

	return (
		<>
			<Head title={t("unassigned_visits.head_title")} />

			<PageContainer className="min-h-[100vh] flex-1 md:min-h-min space-y-4">
				<div>
					<h1 className="text-lg font-bold tracking-tight uppercase">
						{t("unassigned_visits.page_title")}
					</h1>
					<p className="w-full text-sm text-muted-foreground text-pretty md:max-w-[60ch]">
						{t("unassigned_visits.page_description")}
					</p>
				</div>

				<Separator className="bg-border" />

				<div className="z-10 flex gap-2 justify-between">
					<div className="flex items-center gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									disabled={isSorting}
									type="button"
									variant="outline"
									className="shadow-none"
								>
									{isSorting ? (
										<LoaderCircleIcon className="animate-spin" />
									) : (
										<ArrowDownUpIcon />
									)}
									{isSorting ? t("button.sort.loading") : t("button.sort.base")}
									{sortBy && (
										<>
											<Separator orientation="vertical" />
											<Badge className="uppercase text-[10px] p-0 px-1">
												{sortBy?.replaceAll("_", " ")}
											</Badge>
										</>
									)}
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								align="start"
								side="bottom"
								className="w-fit"
							>
								<DropdownMenuLabel>{t("button.sort.label")}</DropdownMenuLabel>
								<DropdownMenuSeparator />
								<DropdownMenuRadioGroup
									value={sortBy}
									onValueChange={onChangeSort}
								>
									{sortList.map((item) => (
										<DropdownMenuRadioItem
											key={item}
											value={item}
											className="uppercase"
										>
											{t(`button.sort.options.${item?.replaceAll("_", " ")}`)}
										</DropdownMenuRadioItem>
									))}
									<DropdownMenuSeparator />
									<DropdownMenuRadioItem value="clear" className="uppercase">
										{t("button.sort.clear")}
									</DropdownMenuRadioItem>
								</DropdownMenuRadioGroup>
							</DropdownMenuContent>
						</DropdownMenu>

						<Button
							type="button"
							variant={isShowFilter ? "default" : "outline"}
							className="shadow-none"
							onClick={(event) => {
								event.preventDefault();
								handleToggleFilter();
							}}
						>
							<ListFilterIcon />
							{isShowFilter ? t("button.close_filter") : t("button.filter")}
						</Button>

						{selectedIds.size > 0 && (
							<div className="flex items-center gap-2 h-full">
								<Separator
									orientation="vertical"
									className="h-[80%] bg-input"
								/>

								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<Badge
										variant="accent"
										className="items-center gap-0.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-sm h-fit"
									>
										<div className="space-x-1.5">
											<span>{selectedIds.size}</span>

											<span className="text-xs font-medium text-primary/80">
												{selectedIds.size === 1
													? t("unassigned_visits.table.selected_count_singular")
													: t("unassigned_visits.table.selected_count_plural", {
															count: selectedIds.size,
														})}
											</span>
										</div>

										<button
											className="-ms-px -me-1.5 inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-[inherit] p-0 text-muted-foreground outline-none transition-[color,box-shadow] hover:text-destructive focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
											onClick={() => setSelectedIds(new Set())}
											type="button"
										>
											<XIcon aria-hidden="true" size={12} />
										</button>
									</Badge>

									<Button
										type="button"
										size="sm"
										className="shadow-none"
										onClick={() => {
											setIsReblastDrawerOpen(true);
											setReblastSubmitError(null);
										}}
									>
										{t("unassigned_visits.reblast.button")}
									</Button>
								</div>
							</div>
						)}
					</div>

					<div>
						<Button
							type="button"
							variant="outline"
							className="shadow-none"
							onClick={() => {
								if (mitraBlastConfigsUrl) {
									router.visit(mitraBlastConfigsUrl);
								}
							}}
						>
							<Settings className="h-4 w-4" />
							{t("unassigned_visits.set_blast_configs")}
						</Button>
					</div>
				</div>

				<AnimatePresence initial={false}>
					{isShowFilter && (
						<motion.section
							initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
							animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
							exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
							transition={{ duration: 0.3, ease: "easeOut" }}
							className="p-3 border shadow-inner rounded-xl bg-background space-y-3"
						>
							<div className="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-4">
								<div className="xl:col-span-2">
									<Input
										type="text"
										className="shadow-inner bg-input"
										placeholder={t("unassigned_visits.search.placeholder")}
										value={searchQuery}
										StartIcon={{ icon: Search }}
										isLoading={isSearching}
										EndIcon={
											searchQuery
												? {
														icon: X,
														isButton: true,
														handleOnClick: () => {
															handleSearchFilter("");
														},
													}
												: undefined
										}
										onChange={(event) => {
											handleSearchFilter(event.target.value);
										}}
									/>
								</div>

								<Popover>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											className={cn(
												"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
												!selectedServiceOptions?.length &&
													"text-muted-foreground",
											)}
										>
											<MultiSelectBadges
												placeholder={t("filter.services.placeholder")}
												maxShows={1}
												items={selectedServiceOptions}
												onRemoveRestAll={(values) => {
													const updatedIds = values
														.map((item) => String(item.id))
														.join(",");
													handleFilterBy({
														type: "serviceIds",
														value: updatedIds,
													});
												}}
												onRemoveSingle={(values) => {
													const updatedIds = values
														.map((item) => String(item.id))
														.join(",");
													handleFilterBy({
														type: "serviceIds",
														value: updatedIds,
													});
												}}
											/>
											<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent
										className="p-0 w-fit"
										align="start"
										side="bottom"
									>
										<Command>
											<CommandInput
												placeholder={t("filter.services.search.placeholder")}
												className="h-9"
											/>
											<CommandList>
												<CommandEmpty>
													{t("filter.services.search.empty")}
												</CommandEmpty>
												<CommandGroup>
													{availableServices?.length ? (
														availableServices?.map((service) => (
															<CommandItem
																key={service.id}
																value={String(service.id)}
																onSelect={() => {
																	const id = String(service.id);
																	handleToggleServiceSelection(id);
																}}
															>
																<span
																	title={service.label}
																	className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
																>
																	{service.label}
																</span>
																<Check
																	className={cn(
																		"ml-auto",
																		serviceIds?.includes(String(service.id))
																			? "opacity-100"
																			: "opacity-0",
																	)}
																/>
															</CommandItem>
														))
													) : (
														<CommandItem value="" disabled>
															<span>{t("filter.services.search.empty")}</span>
														</CommandItem>
													)}
												</CommandGroup>
											</CommandList>
											<CommandList>
												<CommandGroup>
													<CommandSeparator className="mb-2" />
													<CommandItem
														onSelect={() => {
															handleFilterBy({ type: "serviceIds", value: "" });
														}}
													>
														<span className="mx-auto font-medium text-center uppercase w-fit">
															{t("filter.services.search.clear")}
														</span>
													</CommandItem>
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>

								<Popover>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											className={cn(
												"relative w-full flex justify-between font-normal !px-3 bg-input shadow-inner",
												!selectedPackageOptions?.length &&
													"text-muted-foreground",
											)}
										>
											<MultiSelectBadges
												placeholder={t("filter.packages.placeholder")}
												maxShows={1}
												items={selectedPackageOptions}
												onRemoveRestAll={(values) => {
													const updatedIds = values
														.map((item) => String(item.id))
														.join(",");
													handleFilterBy({
														type: "packageIds",
														value: updatedIds,
													});
												}}
												onRemoveSingle={(values) => {
													const updatedIds = values
														.map((item) => String(item.id))
														.join(",");
													handleFilterBy({
														type: "packageIds",
														value: updatedIds,
													});
												}}
											/>
											<ChevronsUpDown className="flex-shrink-0 text-muted-foreground/50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent
										className="p-0 w-fit"
										align="start"
										side="bottom"
									>
										<Command>
											<CommandInput
												placeholder={t("filter.packages.search.placeholder")}
												className="h-9"
											/>
											<CommandList>
												<CommandEmpty>
													{t("filter.packages.search.empty")}
												</CommandEmpty>
												<CommandGroup>
													{groupedPackageOptions?.length ? (
														groupedPackageOptions?.map((service) => (
															<CommandGroup
																key={service.header}
																heading={service.header}
															>
																{service?.packages?.length ? (
																	service.packages.map((pkg) => (
																		<CommandItem
																			key={pkg.id}
																			value={String(pkg.id)}
																			onSelect={() => {
																				handleTogglePackageSelection(
																					String(pkg.id),
																				);
																			}}
																		>
																			<span
																				title={pkg.name}
																				className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap"
																			>
																				{pkg.name.replaceAll("_", " ")}
																			</span>
																			<Check
																				className={cn(
																					"ml-auto",
																					selectedPackageIds?.some(
																						(p) => p === String(pkg.id),
																					)
																						? "opacity-100"
																						: "opacity-0",
																				)}
																			/>
																		</CommandItem>
																	))
																) : (
																	<CommandItem value={""} disabled>
																		<span className="flex-grow-0 text-xs tracking-wide uppercase text-nowrap">
																			{t("filter.packages.search.empty")}
																		</span>
																	</CommandItem>
																)}
															</CommandGroup>
														))
													) : (
														<CommandItem value="" disabled>
															<span>{t("filter.packages.search.empty")}</span>
														</CommandItem>
													)}
												</CommandGroup>
											</CommandList>
											<CommandList>
												<CommandGroup>
													<CommandSeparator className="mb-2" />
													<CommandItem
														onSelect={() => {
															handleFilterBy({ type: "packageIds", value: "" });
														}}
													>
														<span className="mx-auto font-medium text-center uppercase w-fit">
															{t("filter.packages.search.clear")}
														</span>
													</CommandItem>
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
							</div>

							<div className="flex flex-col justify-end gap-4 mt-4 md:gap-2 col-span-full md:flex-row">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="w-full md:w-fit"
									onClick={(event) => {
										event.preventDefault();
										handleToggleFilter();
									}}
								>
									{t("button.close_filter")}
								</Button>

								<Button
									type="button"
									size="sm"
									variant="ghost-destructive"
									className="w-full md:w-fit"
									disabled={isSearching}
									onClick={(event) => {
										event.preventDefault();
										onResetSearch();
									}}
								>
									{t("button.clear_all_filters")}
								</Button>
							</div>
						</motion.section>
					)}
				</AnimatePresence>

				{failedReblastItems.length > 0 ? (
					<Alert variant="destructive">
						<AlertTitle>
							{t("unassigned_visits.reblast.alert.title", {
								count: failedReblastItems.length,
							})}
						</AlertTitle>
						<AlertDescription>
							<ul className="mt-2 space-y-1 list-disc pl-5">
								{failedReblastItems.map((item, index) => {
									const reference = item.registrationNumber
										? `#${item.registrationNumber}`
										: item.appointmentId;

									return (
										<li key={`${item.appointmentId}-${index}`}>
											{t("unassigned_visits.reblast.alert.item", {
												reference,
												message: item.errorMessage,
											})}
										</li>
									);
								})}
							</ul>
						</AlertDescription>
					</Alert>
				) : null}

				<Deferred
					data={["appointments"]}
					fallback={
						<div className="space-y-4">
							<Skeleton className="w-2/12 h-4 rounded-sm" />
							<Skeleton className="relative w-full h-32 rounded-xl" />
						</div>
					}
				>
					<div className="space-y-4 grid grid-cols-1">
						<div className="rounded-md border">
							<Table className="w-full">
								<TableHeader>
									<TableRow>
										<TableHead className="w-fit">
											<TooltipProvider>
												<Tooltip>
													<TooltipTrigger asChild>
														<Checkbox
															checked={allSelected}
															data-state={
																someSelected
																	? "indeterminate"
																	: allSelected
																		? "checked"
																		: "unchecked"
															}
															onCheckedChange={toggleSelectAll}
															aria-label={t(
																"unassigned_visits.table.select_all",
															)}
															className="z-20"
														/>
													</TooltipTrigger>
													<TooltipContent side="top">
														<span>
															{allSelected
																? t("button.unselect_all")
																: t("button.select_all")}
														</span>
													</TooltipContent>
												</Tooltip>
											</TooltipProvider>
										</TableHead>
										<TableHead>{t("unassigned_visits.table.reg_no")}</TableHead>
										<TableHead>
											{t("unassigned_visits.table.visit_no")}
										</TableHead>
										<TableHead>
											{t("unassigned_visits.table.visit_date_time")}
										</TableHead>
										<TableHead>
											{t("unassigned_visits.table.patient")}
										</TableHead>
										<TableHead>
											{t("unassigned_visits.table.service")}
										</TableHead>
										<TableHead>
											{t("unassigned_visits.table.invoice_no")}
										</TableHead>
										<TableHead>
											{t("unassigned_visits.table.created_at")}
										</TableHead>
										<TableHead>
											{t("unassigned_visits.table.latest_blast_at")}
										</TableHead>
										<TableHead>
											{t("unassigned_visits.table.latest_blast_channel")}
										</TableHead>
										<TableHead className="w-fit">
											<span className="sr-only">
												{t("unassigned_visits.table.actions")}
											</span>
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{visits.length > 0 ? (
										visits.map((appointment) => {
											const formattedDateTime = formatDateTime(
												appointment.appointmentDateTime,
												currentLocale,
											);
											const { fullUrl: detailUrl } = populateQueryParams(
												appointment.orderId ? ordersPageUrl : apptPageUrl,
												appointment.orderId
													? {
															search: appointment.registrationNumber,
														}
													: {
															registration_number:
																appointment.registrationNumber,
														},
											);

											return (
												<TableRow key={appointment.id}>
													<TableCell className="w-fit">
														<TooltipProvider>
															<Tooltip>
																<TooltipTrigger asChild>
																	<Checkbox
																		checked={selectedIds.has(appointment.id)}
																		onCheckedChange={() =>
																			toggleSelectOne(appointment.id)
																		}
																		aria-label={t(
																			"unassigned_visits.table.select_one",
																			{ id: appointment.id },
																		)}
																	/>
																</TooltipTrigger>
																<TooltipContent side="top">
																	<span>
																		{selectedIds.has(appointment.id)
																			? t(
																					"unassigned_visits.table.deselect_one",
																					{
																						id: appointment.registrationNumber,
																					},
																				)
																			: t(
																					"unassigned_visits.table.select_one",
																					{
																						id: appointment.registrationNumber,
																					},
																				)}
																	</span>
																</TooltipContent>
															</Tooltip>
														</TooltipProvider>
													</TableCell>
													<TableCell className="font-mono font-semibold text-nowrap">
														<a
															href={detailUrl}
															target="_blank"
															className="text-primary hover:underline hover:text-primary/80"
														>
															#{appointment.registrationNumber}
														</a>
													</TableCell>
													<TableCell className="text-nowrap">
														{t("unassigned_visits.table.visit")}{" "}
														{appointment.visitNumber}/
														{appointment.totalPackageVisits}
													</TableCell>
													<TableCell className="text-nowrap">
														{appointment.appointmentDateTime ? (
															<div>
																<p>{formattedDateTime.date}</p>
																<p className="text-muted-foreground">
																	{formattedDateTime.time}
																</p>
															</div>
														) : (
															"N/A"
														)}
													</TableCell>
													<TableCell>
														<div className="uppercase text-nowrap">
															<p className="font-medium">
																{appointment.patient?.name || "N/A"}
															</p>
															<p className="text-muted-foreground">
																{appointment.location?.city || "N/A"}
															</p>
														</div>
													</TableCell>
													<TableCell>
														<div className="text-nowrap">
															<p>
																{appointment.service?.name?.replaceAll(
																	"_",
																	" ",
																) || "N/A"}
															</p>
															<p className="text-muted-foreground">
																{appointment.package?.name || "N/A"} &mdash;{" "}
																{appointment.totalPackageVisits}{" "}
																{t("unassigned_visits.table.visits")}
															</p>
														</div>
													</TableCell>
													<TableCell className="text-nowrap text-muted-foreground/75 italic">
														{appointment.invoiceNumber || "N/A"}
													</TableCell>
													<TableCell className="text-nowrap text-muted-foreground/75">
														{appointment.createdAt
															? (() => {
																	const dt = formatDateTime(
																		appointment.createdAt,
																		currentLocale,
																	);

																	return (
																		<div>
																			<p>{dt.date}</p>
																			<p>{dt.time}</p>
																		</div>
																	);
																})()
															: "N/A"}
													</TableCell>
													<TableCell className="text-nowrap">
														{appointment.latestBlastAt
															? (() => {
																	const dt = formatDateTime(
																		appointment.latestBlastAt,
																		currentLocale,
																	);

																	return (
																		<div>
																			<p>{dt.date}</p>
																			<p>{dt.time}</p>
																		</div>
																	);
																})()
															: "N/A"}
													</TableCell>
													<TableCell className="text-nowrap uppercase text-muted-foreground/75 italic">
														{appointment.latestBlastChannel || "N/A"}
													</TableCell>
													<TableCell className="w-fit flex items-center justify-end">
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<Button variant="ghost" size="icon">
																	<span className="sr-only">
																		{t("button.actions.label")}
																	</span>
																	<MoreHorizontal className="w-4 h-4" />
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																<DropdownMenuGroup>
																	<DropdownMenuItem
																		onSelect={() => {
																			if (apptPageUrl) {
																				router.visit(
																					`${apptPageUrl}/${appointment.id}/reschedule`,
																				);
																			}
																		}}
																	>
																		<Clock3Icon className="opacity-60" />
																		{t("button.reschedule")}
																	</DropdownMenuItem>
																</DropdownMenuGroup>
															</DropdownMenuContent>
														</DropdownMenu>
													</TableCell>
												</TableRow>
											);
										})
									) : (
										<TableRow>
											<TableCell
												colSpan={11}
												className="h-24 text-center text-muted-foreground/75"
											>
												{t("unassigned_visits.table.empty")}
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>
					</div>
				</Deferred>

				<Deferred
					data={["appointments"]}
					fallback={
						<div className="flex flex-col justify-between gap-6 mt-6 md:flex-row">
							<div className="flex items-center self-end gap-4 md:self-start">
								<Skeleton className="w-[120px] h-4 rounded-sm" />
								<Skeleton className="w-[75px] rounded-sm h-9" />
							</div>
							<div className="flex items-center gap-4">
								<Skeleton className="w-full md:w-[200px] h-4 rounded-sm" />
								<div className="flex items-center gap-2">
									<Skeleton className="w-[35px] rounded-sm h-9" />
									<Skeleton className="w-[35px] rounded-sm h-9" />
								</div>
							</div>
						</div>
					}
				>
					<ApptPagination
						metadata={metadata}
						actions={{
							goToPrevpage: () =>
								updatePageParam({
									page: Math.max(1, (metadata?.page || 1) - 1),
								}),
							goToNextPage: () =>
								updatePageParam({
									page: Math.min(
										metadata?.pages || 1,
										(metadata?.page || 1) + 1,
									),
								}),
							onChangeLimit: (value) =>
								updatePageParam({ page: 1, limit: Number(value) }),
							goToPage: (value) => updatePageParam({ page: Number(value) }),
							goToFirstPage: () => updatePageParam({ page: 1 }),
							goToLastPage: () =>
								updatePageParam({ page: metadata?.pages || 1 }),
						}}
					/>
				</Deferred>

				<Drawer
					open={isReblastDrawerOpen}
					onOpenChange={onReblastDrawerOpenChange}
				>
					<DrawerContent>
						<div className="mx-auto w-full max-w-md">
							<DrawerHeader>
								<DrawerTitle>
									{t("unassigned_visits.reblast.drawer.title")}
								</DrawerTitle>
								<DrawerDescription>
									{t("unassigned_visits.reblast.drawer.description")}
								</DrawerDescription>
							</DrawerHeader>

							<div className="px-4 pb-4 space-y-4">
								<p className="text-sm text-muted-foreground">
									{t("unassigned_visits.reblast.drawer.selected_count", {
										count: selectedIds.size,
									})}
								</p>

								<fieldset className="space-y-4">
									<legend className="font-medium text-foreground text-sm leading-none">
										{t("unassigned_visits.reblast.drawer.channel_label")}
									</legend>
									<RadioGroup
										value={selectedBlastChannel}
										onValueChange={(value) => {
											setSelectedBlastChannel(value as BlastChannel);
										}}
										className="flex flex-wrap gap-2"
									>
										{REBLAST_CHANNEL_OPTIONS.map((channel) => {
											const id = `reblast-channel-${channel.toLowerCase()}`;

											return (
												<div
													className={cn(
														"relative flex flex-col items-start gap-4 rounded-md bg-input border border-input p-3 shadow-none outline-none has-data-[state=checked]:border-primary/50 transition-colors",
														selectedBlastChannel === channel &&
															"bg-primary/10 border-primary/50 text-primary",
													)}
													key={`${id}-${channel}`}
												>
													<div className="flex items-center gap-2">
														<RadioGroupItem
															className="after:absolute after:inset-0"
															id={`${id}-${channel}`}
															value={channel}
														/>
														<Label htmlFor={`${id}-${channel}`}>
															{t(
																`unassigned_visits.reblast.channels.${channel.toLowerCase()}`,
															)}
														</Label>
													</div>
												</div>
											);
										})}
									</RadioGroup>
								</fieldset>

								{reblastSubmitError ? (
									<p className="text-sm text-destructive">
										{reblastSubmitError}
									</p>
								) : null}
							</div>

							<DrawerFooter className="gap-4 md:gap-2 md:flex-row md:justify-end">
								<DrawerClose asChild>
									<Button
										type="button"
										variant="ghost"
										disabled={isSubmittingReblast}
									>
										{t("unassigned_visits.reblast.drawer.buttons.cancel")}
									</Button>
								</DrawerClose>
								<Button
									type="button"
									onClick={submitManualReblast}
									disabled={isSubmittingReblast || selectedIds.size === 0}
								>
									{isSubmittingReblast ? (
										<>
											<LoaderCircleIcon className="size-4 animate-spin" />
											{t("unassigned_visits.reblast.drawer.buttons.submitting")}
										</>
									) : (
										t("unassigned_visits.reblast.drawer.buttons.confirm")
									)}
								</Button>
							</DrawerFooter>
						</div>
					</DrawerContent>
				</Drawer>
			</PageContainer>
		</>
	);
}
